import React, { memo, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import cn from 'classnames';

import { Checkbox, CheckboxOptions, DynamicIconWrapper, Icons, Modal, Tooltip } from '@components/common';
import SitesCellsDataIncluded from '@components/ModalComponents/SitesCellsDataIncluded';

import { useAppDispatch } from '@store/store';

import { centerMapOnSite } from '@slices/map';

import { IChartCardHeaderTypes } from './ChartCardHeader.types';

import { EVendorsValues } from '@enums/common';
import { EAggregationTypes } from '@enums/kpi-api';

import { routes } from '@constants/routes';

import './ChartCardHeader.scss';

const ChartCardHeader = ({
	chart,
	toggleCollapsibleBlock,
	isOpenChart,
	selectedIds,
	setSelectedIds,
	addChartToCompare,
	listOfTooltipCompareMessage,
	isCheckedCompare,
	addChartToCorrelate,
	isCheckedCorrelate,
	listOfTooltipCorrelateMessage,
	onDeleteChart,
	panTo,
}: IChartCardHeaderTypes) => {
	const dispatch = useAppDispatch();
	const navigate = useNavigate();
	const isOranVendor = chart.vendor.name === EVendorsValues.oran;

	const nameRef = useRef<HTMLInputElement | null>(null);

	const [isFullSize, setIsFullSize] = useState(false);
	const [isSitesCellsDataIncludedModalOpen, setIsSitesCellsDataIncludedModalOpen] = useState(false);

	const isSelected = selectedIds.includes(chart.chartId);
	const siteId = chart?.sites[0].siteId;
	const isShowSiteId = chart.aggregationType.name !== EAggregationTypes.aggregated;
	const isShowListOfSites = chart.aggregationType.name !== EAggregationTypes.siteLevelAggregatedCells;

	const getDimensions = () => {
		if (nameRef.current) {
			const isFullSize = nameRef.current?.offsetHeight <= 48;

			setIsFullSize(isFullSize);
		}
	};

	useLayoutEffect(() => {
		getDimensions();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [chart.name, nameRef.current]);

	const nameClassname = cn('f5gChartCardHeaderKpiTitle', {
		'ellipsis': !isFullSize,
	});

	const setDeleteChartId = () => onDeleteChart({ chartId: chart.chartId });

	const onChangeSelect = () => isSelected
		? setSelectedIds(prevState => prevState.filter(id => id !== chart.chartId))
		: setSelectedIds(prevState => ([...prevState, chart.chartId]));

	const toggleAddToCompare = () => addChartToCompare(chart);

	const toggleAddToCorrelate = () => addChartToCorrelate(chart);

	const toggleSitesCellsDataIncludedModalOpen = () => setIsSitesCellsDataIncludedModalOpen(prevState => !prevState);

	const handleViewSiteOnMap = async () => {
		if (chart?.sites[0]) void dispatch(centerMapOnSite(chart?.sites[0]?.siteDbId as string, panTo));

		navigate(routes.map.index, { state: { isRedirectedFromAdminPanel: true } });
	};

	return (
		<div className='f5gChartCardHeaderContainer'>
			{createPortal(
				<Modal
					isOpen={isSitesCellsDataIncludedModalOpen}
					isScrollableContent
					onClose={toggleSitesCellsDataIncludedModalOpen}
					title={chart.name}
					width='700px'
				>
					<SitesCellsDataIncluded charts={[chart]} />
				</Modal>,
				document.body,
			)}
			<div className='f5gChartCardHeaderKpiName'>
				<div className='f5gChartCardHeaderKpiNameCheckbox'>
					<Checkbox
						checked={isSelected}
						name={`select-kpi-${chart.chartId}`}
						onChange={onChangeSelect}
					/>
				</div>
				<Tooltip
					content={chart.name}
					isDisabled={isFullSize}
				>
					<div
						ref={nameRef}
						className={nameClassname}>
						{chart.name}
					</div>
				</Tooltip>
				{isShowListOfSites
					&& <div onClick={toggleSitesCellsDataIncludedModalOpen}>
						<DynamicIconWrapper icon={Icons.manageSiteDB} />
					</div>
				}
				<div onClick={setDeleteChartId}>
					<DynamicIconWrapper icon={Icons.delete} />
				</div>
				<div
					className='f5gChartCardHeaderCollapseIcons'
					data-testid='kpi-collapive-block'
					onClick={toggleCollapsibleBlock}
				>
					{isOpenChart ? Icons.chevronUp() : Icons.chevronDown()}
				</div>
			</div>
			{isShowSiteId
				&& <div className='f5gChartCardSiteId'>
					<span>{isOranVendor ? 'Node' : 'Site'} ID: {siteId}</span>
					{!isOranVendor
						&& <DynamicIconWrapper
							icon={Icons.target}
							onClick={handleViewSiteOnMap}
							style={{ marginLeft: '5px' }}
							withHover
						/>
					}
				</div>
			}
			<CheckboxOptions
				chart={chart}
				isCheckedCompare={isCheckedCompare}
				isCheckedCorrelate={isCheckedCorrelate}
				isDisabledCompare={!!listOfTooltipCompareMessage.length}
				isDisabledCorrelate={!!listOfTooltipCorrelateMessage.length}
				listOfTooltipCompareMessage={listOfTooltipCompareMessage}
				listOfTooltipCorrelateMessage={listOfTooltipCorrelateMessage}
				toggleAddToCompare={toggleAddToCompare}
				toggleAddToCorrelate={toggleAddToCorrelate}
			/>
		</div>
	);
};

export default memo(ChartCardHeader);
